// The walk's model (docs/job-view-brief.md, section 2): every item on the job, add-on items included (an item in an
// add-on is on the job, labelled with its add-on), each read from the resolution that prices it under the open build:
// a base item from the build alone, an add-on item from the build with that one add-on on. The order is the engine's
// walkOrder; an alternate is listed right under the item it stands in for. Nothing here is stored.

import { useMemo } from 'react';
import { checkState, walkOrder, type Library, type Resolution, type ResolvedItem, type WalkStop, type WalkWall } from '@zerogap/engine';
import { pricedUnder, usePricingRows } from '../../../lib/job';
import type { Row } from '../../../lib/api';
import type { JobCtx } from '../Workspace';

export interface WalkModel {
  /** Every item on the job (base and add-on), with the job's groups: what walkOrder and the tree read. */
  res: Resolution;
  /** The resolution that prices an item (its route, BOM and price live there). */
  resOf: (id: string) => Resolution;
  walls: WalkWall[];
  stops: WalkStop[];
  /** The stop an item is walked under: its own, or its top-level parent's. */
  stopOf: (itemId: string) => WalkStop | null;
  /** True when the open build does not carry the item: its add-on does not apply to the build, or its catalog item has no recipe under the build's standards. */
  notIn: (item: ResolvedItem) => boolean;
  /** The heading an item row sits under in the tree: the catalog item's family, its name, or Custom. */
  kindOf: (item: ResolvedItem) => string;
  /** The job_item row behind an item (the check columns live there). */
  rowOf: (id: string) => Row | undefined;
  /** checked · cleared · never, for a stop (the job route and Job BOM read the job's own Verify). */
  stateOf: (stop: WalkStop) => 'checked' | 'cleared' | 'never';
}

/** The add-on applies to this build: its list is empty (every build) or names it. */
export function appliesTo(addOn: Row, buildId: string | null | undefined): boolean {
  const list = Array.isArray(addOn.applies_to) ? (addOn.applies_to as string[]) : [];
  return list.length === 0 || !buildId || list.includes(buildId);
}

/** A short name for an add-on on a tag: its first words, up to the first word that starts describing it. */
export function addOnShort(name: string): string {
  const stop = new Set(['instead', 'over', 'the', 'of', 'for', 'under', 'on', 'in', 'with', 'cabinet', 'cabinets', 'top', 'tops', 'butcher', 'butcherblock', 'wine', 'dividers', 'finish', 'and', 'to', 'a']);
  const words = name.split(/[\s,·]+/).filter(Boolean);
  const out: string[] = [];
  for (const w of words) {
    if (out.length > 0 && stop.has(w.toLowerCase())) break;
    out.push(w);
    if (out.length === 2) break;
  }
  return out.join(' ') || name;
}

export function useWalkModel(ctx: JobCtx): WalkModel {
  const { data, build } = ctx;
  const lib = ctx.r.lib as Library;
  const baseRes = ctx.r.res!;
  // the pricing (every add-on resolved under the build) is keyed on the pricing inputs alone; a Checked mark landing on a
  // row changes only the rows read below, so a check never re-prices the job (the Quick check pack, 2026-10-24)
  const pricingItems = usePricingRows(data.items);
  const priced = useMemo(() => {
    // the base: the open build with no add-on on (the job as the tabs price it when none is picked)
    const base = ctx.addOnsOn.length === 0 ? baseRes : pricedUnder(ctx.asEntered, build, []) ?? baseRes;
    const owner = new Map<string, Resolution>();
    for (const i of base.items) owner.set(i.id, base);
    const extra: ResolvedItem[] = [];
    for (const a of data.addOns) {
      const held = pricingItems.some((x) => x.add_on_id === a.id);
      if (!held) continue;
      const withIt = pricedUnder(ctx.asEntered, build, [a]);
      if (!withIt) continue;
      for (const i of withIt.items) {
        if (i.add_on_id !== a.id || owner.has(i.id)) continue;
        owner.set(i.id, withIt);
        extra.push(i);
      }
    }
    const res: Resolution = { ...base, items: [...base.items, ...extra] };
    const walls = walkOrder(res, data.views as any[], data.marks as any[], lib).map((w) => ({ ...w, stops: altsUnderBase(w.stops) }));
    const stops = walls.flatMap((w) => w.stops);
    const byId = new Map(res.items.map((i) => [i.id, i]));
    const topOf = (id: string): ResolvedItem | undefined => {
      let i = byId.get(id);
      for (let d = 0; i && i.parent_item_id && d < 12; d++) i = byId.get(i.parent_item_id);
      return i;
    };
    const stopById = new Map(stops.filter((s) => s.item).map((s) => [s.item!.id, s]));
    const addOnRow = new Map(data.addOns.map((a) => [a.id as string, a]));
    return {
      res,
      resOf: (id: string) => owner.get(id) ?? base,
      walls,
      stops,
      stopOf: (id: string) => {
        const t = topOf(id);
        return t ? stopById.get(t.id) ?? null : null;
      },
      notIn: (item: ResolvedItem) => {
        const a = item.add_on_id ? addOnRow.get(item.add_on_id) : null;
        if (a && build && !appliesTo(a, build.id)) return true;
        if (item.is_custom || !item.catalog_item_id) return false;
        const cat = lib.catalog[item.catalog_item_id];
        return !!cat && cat.recipes.length > 0 && !item.recipe_id;
      },
      kindOf: (item: ResolvedItem) => (item.is_custom || !item.catalog_item_id ? 'Custom' : lib.catalog[item.catalog_item_id]?.family || item.catalog_item_name || item.name),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRes, ctx.asEntered, build, data.addOns, pricingItems, data.views, data.marks, lib, ctx.addOnsOn.join(',')]);
  // the rows behind the items (the check columns live there), read live: cheap, and re-read on every check
  const rows = useMemo(() => new Map(data.items.map((r) => [r.id as string, r])), [data.items]);
  const jobChecked = !!data.job?.checked_at;
  return useMemo(
    () => ({
      ...priced,
      rowOf: (id: string) => rows.get(id),
      stateOf: (stop: WalkStop) => {
        if (stop.item) return checkState(rows.get(stop.item.id) as any);
        return jobChecked ? 'checked' : 'never';
      },
    }),
    [priced, rows, jobChecked],
  );
}

/** An alternate is listed right under the item it stands in for, when both are on the same wall. */
function altsUnderBase(stops: WalkStop[]): WalkStop[] {
  const alts = stops.filter((s) => s.item?.replaces_item_id && stops.some((b) => b.item?.id === s.item!.replaces_item_id));
  if (alts.length === 0) return stops;
  const out: WalkStop[] = [];
  for (const s of stops) {
    if (alts.includes(s)) continue;
    out.push(s);
    for (const a of alts) if (a.item!.replaces_item_id === s.item?.id) out.push(a);
  }
  return out;
}

/**
 * Each Build's resolution, alone or with one add-on on, worked out once while the job stays as it is: the Build by
 * build strip reads the open item under every build.
 */
export function useBuildPricing(ctx: JobCtx): (build: Row, addOnId: string | null) => Resolution | null {
  return useMemo(() => {
    const cache = new Map<string, Resolution | null>();
    return (build: Row, addOnId: string | null) => {
      const k = `${build.id}|${addOnId ?? ''}`;
      if (!cache.has(k)) {
        const a = addOnId ? ctx.data.addOns.filter((x) => x.id === addOnId) : [];
        cache.set(k, pricedUnder(ctx.asEntered, build, a));
      }
      return cache.get(k) ?? null;
    };
  }, [ctx.asEntered, ctx.data.addOns]);
}

/** The minutes and lines an item takes in its own group: its share of every step and line. */
export function itemShare(res: Resolution, item: ResolvedItem) {
  const group = res.groups.find((g) => g.key === item.group_key);
  const steps = (group?.steps ?? []).filter((st) => Number(st.shares[item.id]?.minutes ?? 0) > 0 || Number(st.shares[item.id]?.qty ?? 0) > 0);
  const bom = (group?.bom ?? []).filter((b) => (b.shares[item.id]?.cents ?? 0) > 0 || Number(b.shares[item.id]?.qty ?? 0) > 0 || (Number(b.qty) > 0 && group!.item_ids.length === 1));
  const minutes = steps.reduce((n, st) => n + Number(st.shares[item.id]?.minutes ?? 0), 0);
  const cents = bom.reduce((n, b) => n + (b.shares[item.id]?.cents ?? (group!.item_ids.length === 1 ? b.cents : 0)), 0);
  return { group, steps, bom, minutes, cents };
}

/** The short name of a View: the words before " · " (Wall 1 · folding and laundry wall → Wall 1). */
export function wallShort(name: string | null | undefined): string {
  const n = String(name ?? 'View');
  const at = n.indexOf(' · ');
  return at > 0 ? n.slice(0, at) : n;
}

/** An operation's unit in words: /box → per box, /pc → per piece. */
export function unitWords(unit: string | null | undefined): string {
  if (!unit) return '';
  const u = unit.trim().replace(/^\//, '').toLowerCase();
  const word = u === 'pc' ? 'piece' : u === 'hr' ? 'hour' : u === 'min' ? 'minute' : u === 'ft' ? 'foot' : u;
  if (u === 'standard') return 'standard';
  return `per ${word}`;
}
